/**
 * 003 — Gesellschafter & Regeln (US1/US2, FR-018/FR-019): the tenant's GbR
 * partners with their private IBANs (GF writes, Stbr reads) and the read-only
 * list of reconciliation rules. Rules are created from a confirmed movement in
 * the Bankbuchungen tab; full rule editing is later (US7).
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, GlassPanel, Input, LoadingSpinner, StatusBadge } from '../ui';
import { ApiError } from '../../services/api';
import { apiErrorMessage } from '../../lib/api-error-message';
import { CLASSIFICATION_LABELS, bankApi, bankKeys, ibanLabel, money, type Partner, type Rule } from '../../lib/bank-api';
import { errorText } from './ReviewEditor';
import type { BankTabProps } from './types';

const TEXTAREA_CLASS =
  'block w-full rounded-lg border border-white/[0.08] bg-white/[0.03] p-3 text-white placeholder:text-white/30 focus:border-white/20 focus:outline-none';

const parseIbans = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

/** Mirrors `ibans: z.array(ibanSchema).max(10)`; zod's own max message is English. */
const MAX_IBANS = 10;
const TOO_MANY_IBANS = `Höchstens ${MAX_IBANS} IBANs je Gesellschafter.`;

const DIRECTION_LABELS: Record<Rule['direction'], string> = {
  in: 'Eingang',
  out: 'Ausgang',
  any: 'Ein- und Ausgang',
};

const classificationLabel = (c: string) => (CLASSIFICATION_LABELS as Record<string, string | undefined>)[c] ?? c;

function amountRange(r: Rule): string | null {
  if (r.minCents !== null && r.maxCents !== null) return `Betrag ${money(r.minCents)} – ${money(r.maxCents)}`;
  if (r.minCents !== null) return `Betrag ab ${money(r.minCents)}`;
  if (r.maxCents !== null) return `Betrag bis ${money(r.maxCents)}`;
  return null;
}

function conditionParts(r: Rule): string[] {
  return [
    r.counterpartyIban ? `IBAN ${ibanLabel(r.counterpartyIban)}` : null,
    r.counterpartyNameContains ? `Name enthält „${r.counterpartyNameContains}“` : null,
    r.referenceContains ? `Verwendungszweck enthält „${r.referenceContains}“` : null,
    DIRECTION_LABELS[r.direction],
    amountRange(r),
  ].filter((p): p is string => p !== null);
}

function outcomeParts(r: Rule, partners: Partner[]): string[] {
  const partner = r.outcomePartnerId
    ? `Gesellschafter ${partners.find((p) => p.id === r.outcomePartnerId)?.displayName ?? 'unbekannt'}`
    : null;
  return [
    r.outcomeClassification ? classificationLabel(r.outcomeClassification) : null,
    partner,
    r.outcomeSupplierId ? 'Lieferant hinterlegt' : null,
    r.outcomeCategoryId ? 'Kategorie hinterlegt' : null,
  ].filter((p): p is string => p !== null);
}

function CreatePartnerForm({ onNotice }: { onNotice: (m: string) => void }) {
  const [displayName, setDisplayName] = useState('');
  const [ibans, setIbans] = useState('');
  const [error, setError] = useState('');
  const client = useQueryClient();
  const create = useMutation({
    mutationFn: () => bankApi.createPartner({ displayName: displayName.trim(), ibans: parseIbans(ibans) }),
    onSuccess: (partner) => {
      void client.invalidateQueries({ queryKey: bankKeys.all });
      setDisplayName('');
      setIbans('');
      setError('');
      onNotice(`Gesellschafter ${partner.displayName} angelegt.`);
    },
    // apiErrorMessage reads zod-validator issues (e.g. "Ungültige IBAN"), which have no top-level message.
    onError: (e) => setError(apiErrorMessage(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || create.isPending) return;
    if (parseIbans(ibans).length > MAX_IBANS) {
      setError(TOO_MANY_IBANS);
      return;
    }
    create.mutate();
  };
  return (
    <form onSubmit={submit} className="mt-4 space-y-3" data-testid="partner-create-form">
      <h3 className="font-medium">Gesellschafter anlegen</h3>
      <Input label="Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={120} />
      <label className="block text-sm text-white/70">
        IBANs (eine pro Zeile)
        <textarea
          className={`${TEXTAREA_CLASS} mt-1.5`}
          rows={3}
          value={ibans}
          onChange={(e) => setIbans(e.target.value)}
          placeholder="DE88 1001 0010 1000 0000 01"
        />
      </label>
      {error && (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      )}
      <Button type="submit" disabled={!displayName.trim()} loading={create.isPending}>
        Gesellschafter anlegen
      </Button>
    </form>
  );
}

function PartnerRow({
  partner,
  canManage,
  onNotice,
}: {
  partner: Partner;
  canManage: boolean;
  onNotice: (m: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [ibans, setIbans] = useState(partner.ibans.join('\n'));
  // The version the IBAN draft is based on: a background refetch must not bump it (409 stays a 409).
  const [draftVersion, setDraftVersion] = useState(partner.version);
  const [error, setError] = useState('');
  const client = useQueryClient();
  const update = useMutation({
    mutationFn: (patch: { ibans?: string[]; active?: boolean }) =>
      bankApi.updatePartner(partner.id, {
        expectedVersion: patch.ibans !== undefined ? draftVersion : partner.version,
        ...patch,
      }),
    onSuccess: (updated, patch) => {
      void client.invalidateQueries({ queryKey: bankKeys.all });
      setError('');
      setEditing(false);
      onNotice(
        patch.active === undefined
          ? `IBANs von ${updated.displayName} gespeichert.`
          : `${updated.displayName} ${patch.active ? 'reaktiviert' : 'deaktiviert'}.`
      );
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        void client.invalidateQueries({ queryKey: bankKeys.all });
        setEditing(false);
        setError('Gesellschafter inzwischen geändert; die Ansicht wurde neu geladen. Bitte erneut bearbeiten.');
      } else setError(apiErrorMessage(e));
    },
  });
  const saveIbans = () => {
    const list = parseIbans(ibans);
    if (list.length > MAX_IBANS) {
      setError(TOO_MANY_IBANS);
      return;
    }
    update.mutate({ ibans: list });
  };
  const startEdit = () => {
    setIbans(partner.ibans.join('\n'));
    setDraftVersion(partner.version);
    setError('');
    setEditing(true);
  };
  return (
    <li className="rounded-lg border border-white/10 p-3" data-testid="partner-row">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{partner.displayName}</span>
        <StatusBadge tone={partner.active ? 'success' : 'muted'}>{partner.active ? 'Aktiv' : 'Inaktiv'}</StatusBadge>
      </div>
      {editing ? (
        <div className="mt-3 space-y-2">
          <label className="block text-sm text-white/70">
            IBANs von {partner.displayName} (eine pro Zeile)
            <textarea
              className={`${TEXTAREA_CLASS} mt-1.5`}
              rows={3}
              value={ibans}
              onChange={(e) => setIbans(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={update.isPending} onClick={saveIbans}>
              IBANs speichern
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      ) : (
        <ul className="mt-2 text-sm text-white/80">
          {partner.ibans.map((iban) => (
            <li key={iban} className="break-all">
              {ibanLabel(iban)}
            </li>
          ))}
          {partner.ibans.length === 0 && <li className="text-white/50">Keine IBAN hinterlegt</li>}
        </ul>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-300">
          {error}
        </p>
      )}
      {canManage && !editing && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={startEdit} aria-label={`IBANs von ${partner.displayName} bearbeiten`}>
            IBANs bearbeiten
          </Button>
          <Button
            size="sm"
            variant={partner.active ? 'danger' : 'secondary'}
            disabled={update.isPending}
            onClick={() => update.mutate({ active: !partner.active })}
            aria-label={`${partner.displayName} ${partner.active ? 'deaktivieren' : 'reaktivieren'}`}
          >
            {partner.active ? 'Deaktivieren' : 'Reaktivieren'}
          </Button>
        </div>
      )}
    </li>
  );
}

function RulesSection({ tenantId, partners }: { tenantId: string | undefined; partners: Partner[] }) {
  const query = useQuery({
    queryKey: bankKeys.rules(tenantId),
    queryFn: () => bankApi.rules(),
    enabled: Boolean(tenantId),
  });
  const rules = query.data ?? [];
  return (
    <GlassPanel data-testid="rules-panel">
      <h2 className="text-lg font-semibold">Abgleichsregeln</h2>
      <p className="my-2 text-sm text-white/70">
        Regeln entstehen aus bestätigten Buchungen im Tab Bankbuchungen. Sie erzeugen nur Vorschläge; bestätigt wird
        immer von Ihnen.
      </p>
      {query.isLoading ? (
        <LoadingSpinner />
      ) : query.error ? (
        <p role="alert" className="rounded border border-red-400/40 p-3">
          {errorText(query.error)}
        </p>
      ) : !query.data ? null : rules.length === 0 ? (
        <p className="text-sm text-white/50">Noch keine Abgleichsregeln.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Name</th>
                <th className="text-left">Bedingung</th>
                <th className="text-left">Ergebnis</th>
                <th className="text-right">Treffer</th>
                <th className="text-left">Aktiv</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} data-testid="rule-row" className="align-top">
                  <td>{r.name}</td>
                  <td>
                    <ul>
                      {conditionParts(r).map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <ul>
                      {outcomeParts(r, partners).map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="text-right">{r.matchCount}</td>
                  <td>
                    <StatusBadge tone={r.active ? 'success' : 'muted'}>{r.active ? 'Aktiv' : 'Inaktiv'}</StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassPanel>
  );
}

export function PartnersPanel(props: BankTabProps) {
  const query = useQuery({
    queryKey: bankKeys.partners(props.tenantId),
    queryFn: () => bankApi.partners(),
    enabled: Boolean(props.tenantId),
  });
  const partners = query.data ?? [];
  return (
    <div className="space-y-6">
      <GlassPanel data-testid="partners-panel">
        <h2 className="text-lg font-semibold">Gesellschafter</h2>
        <p className="my-2 text-sm text-white/70">
          Gesellschafter der GbR für Einlagen, Entnahmen und Auslagenerstattungen. Ihre privaten IBANs helfen, solche
          Buchungen als Vorschlag zu erkennen.
        </p>
        {!props.canManage && (
          <p className="text-sm text-amber-200">Nur die Geschäftsführung kann Gesellschafter pflegen.</p>
        )}
        {query.isLoading ? (
          <LoadingSpinner />
        ) : query.error ? (
          <p role="alert" className="rounded border border-red-400/40 p-3">
            {errorText(query.error)}
          </p>
        ) : !query.data ? null : partners.length === 0 ? (
          <EmptyState title="Noch keine Gesellschafter angelegt." />
        ) : (
          <ul className="mt-3 grid gap-3 md:grid-cols-2">
            {partners.map((p) => (
              <PartnerRow key={p.id} partner={p} canManage={props.canManage} onNotice={props.onNotice} />
            ))}
          </ul>
        )}
        {props.canManage && <CreatePartnerForm onNotice={props.onNotice} />}
      </GlassPanel>
      <RulesSection tenantId={props.tenantId} partners={partners} />
    </div>
  );
}
